import React from "react";
import { useOperatorContext } from "@src/context/operatorContext";
import { useAuthContext } from "@src/context/authContext";
import { useHeaderCalloutContext } from "@src/context/headerCalloutContext";
import { SignolUtils, AppUtils } from "@src/utils";

const SESSION_KEY = "sendgrid_crew_update_shown";

/**
 * Renders nothing itself — surfaces its message via HeaderCalloutContext so
 * it appears anchored to the avatar button in AppHeader, which lives outside
 * this component's subtree.
 */
const SendgridCrewUpdateToast = () => {
  const [visible, setVisible] = React.useState(false);
  const [crewUpdateUrl, setCrewUpdateUrl] = React.useState("");
  const { logout } = useAuthContext();
  const { setHeaderCallout } = useHeaderCalloutContext();
  const {
    profile,
    roleAlias,
    getFirstName,
    getLastName,
    getLatestVehicle,
    latestEventsPending,
  } = useOperatorContext();

  React.useEffect(() => {
    if (profile?.customer?.industry !== "shipping") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_source") !== "sendgrid") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    if (latestEventsPending) return;

    const latestVehicle = getLatestVehicle();
    const tokenData = {
      operatorUsername: profile?.appUser?.username,
      customerCode: profile?.customer?.customerCode,
      industry: profile?.customer?.industry,
      ...(latestVehicle?.name && { currentVesselName: latestVehicle.name }),
      ...(latestVehicle?.id && { imo: latestVehicle.id }),
    };

    const url = SignolUtils.generateUrlWithEncodedTokenParams(
      "/crewUpdate",
      tokenData,
    );
    setCrewUpdateUrl(url);
    sessionStorage.setItem(SESSION_KEY, "1");
    setVisible(true);
  }, [profile, latestEventsPending, getLatestVehicle]);

  const firstName = getFirstName();
  const lastName = getLastName();
  const nameDisplay =
    `${firstName?.[0] ? `${firstName[0]}. ` : ""}${lastName}`.trim();

  React.useEffect(() => {
    if (!visible) return;

    setHeaderCallout({
      direction: "down-left",
      bgColor: "primary-100",
      onDismiss: () => setVisible(false),
      content: (
        <>
          <p className="text-sm font-bold">
            Not {AppUtils.formatToTitleCase(roleAlias)} {nameDisplay}?
          </p>
          <p className="text-sm">
            <a
              href={crewUpdateUrl}
              className="underline text-primary-500 hover:text-primary-700"
              onClick={(e) => {
                e.preventDefault();
                logout();
                window.location.href = crewUpdateUrl;
              }}
            >
              Notify us
            </a>{" "}
            of your rotation.
          </p>
        </>
      ),
    });

    return () => setHeaderCallout(null);
  }, [
    visible,
    crewUpdateUrl,
    roleAlias,
    nameDisplay,
    logout,
    setHeaderCallout,
  ]);

  return null;
};

export default SendgridCrewUpdateToast;
